/** @jsxImportSource theme-ui */
import { get, Divider, Link } from 'theme-ui';
import { Box, Container, Button } from 'theme-ui';
import { useLocation, useMatch } from 'react-router-dom';
import { useActor, useSelector } from '@xstate/react';
import { ArrowBarLeft, ArrowBarRight } from 'tabler-icons-react';
import { TrashIcon } from '@heroicons/react/outline';
import { TrashIcon as TrashIconSolid } from '@heroicons/react/solid';
import CustomLink from '@/components/Link';
import { useUiService } from '@/providers/UIProvider';
import { getNavState, NAV_TOGGLE } from '@/machines/uiMachine';
import { getIconForRoute } from '@/utils/routesIcons';
import User from '../user/User';

const [HomeIcon, HomeIconSolid] = getIconForRoute('/');
const [OfficeBuildingIcon, OfficeBuildingIconSolid] = getIconForRoute('/companies');
const [CalendarIcon, CalendarIconSolid] = getIconForRoute('/bookings');
const [UsersIcon, UsersIconSolid] = getIconForRoute('/users');
const [TicketIcon, TicketIconSolid] = getIconForRoute('/tickets');
const [BellIcon, BellIconSolid] = getIconForRoute('/incentives');
const [SparklesIcon, SparklesIconSolid] = getIconForRoute('/actions');
const [CalculatorIcon, CalculatorIconSolid] = getIconForRoute('/voss');
const [ViewListIcon, ViewListIconSolid] = getIconForRoute('/errorlogs');

const SideNav: React.FC = ({ children }) => {
    const uiService = useUiService();
    const isSubnavExpanded = useSelector(uiService, getNavState);
    const collapsed = !isSubnavExpanded;

    const toggleCollapse = () => uiService.send(NAV_TOGGLE);

    const [HomeIcon, HomeIconSolid] = getIconForRoute('/');
    const [OfficeBuildingIcon, OfficeBuildingIconSolid] = getIconForRoute('/companies');
    const [CalendarIcon, CalendarIconSolid] = getIconForRoute('/bookings');
    const [UsersIcon, UsersIconSolid] = getIconForRoute('/users');
    const [CustomerIcon, CustomerIconSolid] = getIconForRoute('/customers');
    const [TicketIcon, TicketIconSolid] = getIconForRoute('/tickets');
    const [BellIcon, BellIconSolid] = getIconForRoute('/incentives');
    const [SparklesIcon, SparklesIconSolid] = getIconForRoute('/actions');
    const [MailIcon, MailIconSolid] = getIconForRoute('/mailchimp');
    const [CodeIcon, CodeIconSolid] = getIconForRoute('/widget/configuration-schemas');
    const [AdjustmentsIcon, AdjustmentsIconSolid] = getIconForRoute('/feature-flags');
    const [ChipIcon, ChipIconSolid] = getIconForRoute('/assistant-usage');
    const [RedirectIcon, RedirectIconSolid] = getIconForRoute('/redirect-domains');
    const [CalculatorIcon, CalculatorIconSolid] = getIconForRoute('/voss');
    const [SpeakerphoneIcon, SpeakerphoneIconSolid] = getIconForRoute('/newsletter');

    return (
        <Container
            bg="background"
            p="2"
            sx={{
                width: collapsed
                    ? (theme) => get(theme, 'sizes.navCollapsed')
                    : (theme) => get(theme, 'sizes.navExpanded'),
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: '100vh',
                fontSize: '14px',
            }}
        >
            <Box sx={{ position: 'sticky', top: 0 }}>
                <CustomLink
                    collapsed={collapsed}
                    // icon={<Home size={20} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />}

                    icon={[
                        <HomeIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <HomeIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/"
                    label="Home"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <OfficeBuildingIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <OfficeBuildingIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/clients"
                    label="Clients"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <CustomerIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <CustomerIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/customers"
                    label="Customers"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <CalendarIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <CalendarIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/bookings"
                    label="Bookings"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <TicketIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <TicketIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/tickets"
                    label="Tickets"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <BellIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <BellIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    label="Incentives"
                    isNavLinkAware={false}
                />
                {!collapsed && (
                    <Box sx={{ ml: 4 }}>
                        <CustomLink
                            collapsed={collapsed}
                            icon={[
                                <BellIcon
                                    width={24}
                                    height={24}
                                    sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                                />,
                                <BellIconSolid
                                    width={24}
                                    height={24}
                                    sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                                />,
                            ]}
                            to="/incentives"
                            label="Incentives"
                        />
                        <CustomLink
                            collapsed={collapsed}
                            icon={[
                                <SparklesIcon
                                    width={24}
                                    height={24}
                                    sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                                />,
                                <SparklesIconSolid
                                    width={24}
                                    height={24}
                                    sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                                />,
                            ]}
                            to="/actions"
                            label="Actions"
                        />
                    </Box>
                )}
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <MailIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <MailIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/mailchimp"
                    label="Mailchimp"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <SpeakerphoneIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <SpeakerphoneIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/newsletter"
                    label="Newsletter"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <CodeIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <CodeIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/widget/configuration-schemas"
                    label="Widget schemas"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <AdjustmentsIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <AdjustmentsIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/feature-flags"
                    label="Feature flags"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <ChipIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <ChipIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/assistant-usage"
                    label="Assistant usage"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <RedirectIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <RedirectIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/redirect-domains"
                    label="Redirect domains"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <TrashIcon width={24} height={24} sx={{ mr: collapsed ? 0 : 2, p: 2 }} />,
                        <TrashIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    to="/deletion-schedules"
                    label="Scheduled deletions"
                />
                <CustomLink
                    collapsed={collapsed}
                    icon={[
                        <CalculatorIcon
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                        <CalculatorIconSolid
                            width={24}
                            height={24}
                            sx={{ mr: collapsed ? 0 : 2, p: 2 }}
                        />,
                    ]}
                    label="Voss"
                >
                    <CustomLink collapsed={collapsed} to="/voss/balance" label="Balance" />
                    <CustomLink
                        collapsed={collapsed}
                        to="/voss/discount-agreements"
                        label="Discount agreements"
                    />
                    <CustomLink collapsed={collapsed} to="/voss/invoices" label="Invoices" />
                    <CustomLink
                        collapsed={collapsed}
                        to="/licensechangeslog"
                        label="License changes log"
                    />
                    <CustomLink collapsed={collapsed} to="/errorlogs" label="Error logs" />
                    <CustomLink collapsed={collapsed} to="/voss/usage" label="Usages" />
                    <CustomLink collapsed={collapsed} to="/voss/usage-audit" label="Usage audit" />
                    <CustomLink
                        collapsed={collapsed}
                        to="/voss/subscription-audit"
                        label="Subscription audit"
                    />
                    <CustomLink collapsed={collapsed} to="/voss/tools" label="Tools" />
                </CustomLink>

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Button
                        variant="minimal"
                        onClick={toggleCollapse}
                        sx={{ mb: 3, cursor: 'pointer' }}
                    >
                        {collapsed ? (
                            <ArrowBarRight sx={{ color: 'gray.9' }} />
                        ) : (
                            <ArrowBarLeft sx={{ color: 'gray.9' }} />
                        )}
                    </Button>
                    <User collapsed={collapsed} />
                </Box>
            </Box>
        </Container>
    );
};

export default SideNav;
